/**
 * Master/Sub-Account Redux Slice
 * 
 * This slice manages all master/sub-account related state and operations,
 * integrating with the masterSubAccountApi service layer.
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import masterSubAccountApi, {
  SubAccountCredentials,
  CreateSubAccountPayload,
  ChangeSubAccountPasswordPayload,
  MoveSmartProbePayload,
  MasterAccountInfo,
  EnhancedUserInfo,
  MasterSubAccountApiError
} from '../../services/masterSubAccountApi';
import { logger } from '../../utils/logger';

// ============================================================================
// STATE INTERFACES
// ============================================================================

interface MasterSubAccountState {
  // Account type and authentication
  accountType: 'master' | 'sub' | 'regular';
  isMasterAccount: boolean;
  isSubAccount: boolean;
  
  // Sub-account specific data
  parentMasterUID: string | null;
  parentMasterEmail: string | null;
  deactivated: boolean;
  
  // Master account data
  masterAccountInfo: MasterAccountInfo | null;
  
  // Enhanced user information
  enhancedUserInfo: EnhancedUserInfo | null;
  
  // Team and probe management
  teamProbesData: any | null;
  allTeamsData: Array<{
    id: string;
    name: string;
    displayName: string;
    company: string;
    type: 'master' | 'sub';
    isActive: boolean;
    probesCount: number;
    createdAt?: string;
  }> | null;
  
  // Loading states
  loading: {
    userInfo: boolean;
    subAccounts: boolean;
    createSubAccount: boolean;
    toggleStatus: boolean;
    changePassword: boolean;
    deleteSubAccount: boolean;
    moveProbe: boolean;
    teamProbes: boolean;
  };
  
  // Error states
  errors: {
    userInfo: string | null;
    subAccounts: string | null;
    createSubAccount: string | null;
    toggleStatus: string | null;
    changePassword: string | null;
    deleteSubAccount: string | null;
    moveProbe: string | null;
    teamProbes: string | null;
  };
  
  // UI state
  selectedSubAccountUID: string | null;
  selectedTeamName: string | null;
  
  // Success messages for UI feedback
  successMessages: {
    createSubAccount: string | null;
    toggleStatus: string | null;
    changePassword: string | null;
    deleteSubAccount: string | null;
    moveProbe: string | null;
  };
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: MasterSubAccountState = {
  accountType: 'regular',
  isMasterAccount: false,
  isSubAccount: false,
  parentMasterUID: null,
  parentMasterEmail: null,
  deactivated: false,
  masterAccountInfo: null,
  enhancedUserInfo: null,
  teamProbesData: null,
  allTeamsData: null,
  loading: {
    userInfo: false,
    subAccounts: false,
    createSubAccount: false,
    toggleStatus: false,
    changePassword: false,
    deleteSubAccount: false,
    moveProbe: false,
    teamProbes: false,
  },
  errors: {
    userInfo: null,
    subAccounts: null,
    createSubAccount: null,
    toggleStatus: null,
    changePassword: null,
    deleteSubAccount: null,
    moveProbe: null,
    teamProbes: null,
  },
  selectedSubAccountUID: null,
  selectedTeamName: null,
  successMessages: {
    createSubAccount: null,
    toggleStatus: null,
    changePassword: null,
    deleteSubAccount: null,
    moveProbe: null,
  },
};

// ============================================================================
// ASYNC THUNKS
// ============================================================================

/**
 * Sub-account login
 */
export const subAccountLogin = createAsyncThunk(
  'masterSubAccount/subAccountLogin',
  async (credentials: SubAccountCredentials, { rejectWithValue }) => {
    try {
      const response = await masterSubAccountApi.auth.subAccountLogin(credentials);
      return response;
    } catch (error) {
      if (error instanceof MasterSubAccountApiError) {
        return rejectWithValue({
          message: error.message,
          status: error.status,
          code: error.code
        });
      }
      return rejectWithValue({
        message: 'Sub-account login failed',
        status: 500,
        code: 'UNKNOWN_ERROR'
      });
    }
  }
);

/**
 * Fetch enhanced user information
 */
export const fetchEnhancedUserInfo = createAsyncThunk(
  'masterSubAccount/fetchEnhancedUserInfo',
  async (_, { rejectWithValue }) => {
    try {
      const response = await masterSubAccountApi.auth.getEnhancedUserInfo();
      return response.userInfo;
    } catch (error) {
      if (error instanceof MasterSubAccountApiError) {
        return rejectWithValue({
          message: error.message,
          status: error.status,
          code: error.code
        });
      }
      return rejectWithValue({
        message: 'Failed to fetch user information',
        status: 500,
        code: 'UNKNOWN_ERROR'
      });
    }
  }
);

/**
 * Create sub-account
 */
export const createSubAccount = createAsyncThunk(
  'masterSubAccount/createSubAccount',
  async (payload: CreateSubAccountPayload, { rejectWithValue }) => {
    try {
      const response = await masterSubAccountApi.masterAccount.createSubAccount(payload);
      return response;
    } catch (error) {
      if (error instanceof MasterSubAccountApiError) {
        return rejectWithValue({
          message: error.message,
          status: error.status,
          code: error.code
        });
      }
      return rejectWithValue({
        message: 'Failed to create sub-account',
        status: 500,
        code: 'UNKNOWN_ERROR'
      });
    }
  }
);

/**
 * Fetch my sub-accounts
 */
export const fetchMySubAccounts = createAsyncThunk(
  'masterSubAccount/fetchMySubAccounts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await masterSubAccountApi.masterAccount.getMySubAccounts();
      return response;
    } catch (error) {
      if (error instanceof MasterSubAccountApiError) {
        return rejectWithValue({
          message: error.message,
          status: error.status,
          code: error.code
        });
      }
      return rejectWithValue({
        message: 'Failed to fetch sub-accounts',
        status: 500,
        code: 'UNKNOWN_ERROR'
      });
    }
  }
);

/**
 * Toggle sub-account status
 */
export const toggleSubAccountStatus = createAsyncThunk(
  'masterSubAccount/toggleSubAccountStatus',
  async (subAccountUID: string, { rejectWithValue }) => {
    try {
      const response = await masterSubAccountApi.masterAccount.toggleSubAccountStatus(subAccountUID);
      return { ...response, subAccountUID };
    } catch (error) {
      if (error instanceof MasterSubAccountApiError) {
        return rejectWithValue({
          message: error.message,
          status: error.status,
          code: error.code
        });
      }
      return rejectWithValue({
        message: 'Failed to toggle sub-account status',
        status: 500,
        code: 'UNKNOWN_ERROR'
      });
    }
  }
);

/**
 * Change sub-account password
 */
export const changeSubAccountPassword = createAsyncThunk(
  'masterSubAccount/changeSubAccountPassword',
  async (payload: ChangeSubAccountPasswordPayload, { rejectWithValue }) => {
    try {
      const response = await masterSubAccountApi.masterAccount.changeSubAccountPassword(payload);
      return { ...response, targetSubAccountUID: payload.targetSubAccountUID };
    } catch (error) {
      if (error instanceof MasterSubAccountApiError) {
        return rejectWithValue({
          message: error.message,
          status: error.status,
          code: error.code
        });
      }
      return rejectWithValue({
        message: 'Failed to change sub-account password',
        status: 500,
        code: 'UNKNOWN_ERROR'
      });
    }
  }
);

/**
 * Delete sub-account
 */
export const deleteSubAccount = createAsyncThunk(
  'masterSubAccount/deleteSubAccount',
  async (subAccountUID: string, { rejectWithValue }) => {
    try {
      const response = await masterSubAccountApi.masterAccount.deleteSubAccount(subAccountUID);
      return { ...response, deletedSubAccountUID: subAccountUID };
    } catch (error) {
      if (error instanceof MasterSubAccountApiError) {
        return rejectWithValue({
          message: error.message,
          status: error.status,
          code: error.code
        });
      }
      return rejectWithValue({
        message: 'Failed to delete sub-account',
        status: 500,
        code: 'UNKNOWN_ERROR'
      });
    }
  }
);

/**
 * Move SmartProbe between teams
 */
export const moveSmartProbe = createAsyncThunk(
  'masterSubAccount/moveSmartProbe',
  async (payload: MoveSmartProbePayload, { rejectWithValue }) => {
    try {
      const response = await masterSubAccountApi.smartProbe.moveSmartProbe(payload);
      return response;
    } catch (error) {
      if (error instanceof MasterSubAccountApiError) {
        return rejectWithValue({
          message: error.message,
          status: error.status,
          code: error.code
        });
      }
      return rejectWithValue({
        message: 'Failed to move SmartProbe',
        status: 500,
        code: 'UNKNOWN_ERROR'
      });
    }
  }
);

/**
 * Fetch team probes
 */
export const fetchTeamProbes = createAsyncThunk(
  'masterSubAccount/fetchTeamProbes',
  async (teamName: string, { rejectWithValue }) => {
    try {
      const response = await masterSubAccountApi.smartProbe.getTeamProbes(teamName);
      return { ...response, teamName };
    } catch (error) {
      if (error instanceof MasterSubAccountApiError) {
        return rejectWithValue({
          message: error.message,
          status: error.status,
          code: error.code
        });
      }
      return rejectWithValue({
        message: 'Failed to fetch team probes',
        status: 500,
        code: 'UNKNOWN_ERROR'
      });
    }
  }
);

// ============================================================================
// SLICE DEFINITION
// ============================================================================

const masterSubAccountSlice = createSlice({
  name: 'masterSubAccount',
  initialState,
  reducers: {
    // Clear all errors
    clearErrors: (state) => {
      state.errors = {
        userInfo: null,
        subAccounts: null,
        createSubAccount: null,
        toggleStatus: null,
        changePassword: null,
        deleteSubAccount: null,
        moveProbe: null,
        teamProbes: null,
      };
    },
    
    // Clear specific error
    clearError: (state, action: PayloadAction<keyof typeof state.errors>) => {
      state.errors[action.payload] = null;
    },
    
    // Clear all success messages
    clearSuccessMessages: (state) => {
      state.successMessages = {
        createSubAccount: null,
        toggleStatus: null,
        changePassword: null,
        deleteSubAccount: null,
        moveProbe: null,
      };
    },
    
    // Clear specific success message
    clearSuccessMessage: (state, action: PayloadAction<keyof typeof state.successMessages>) => {
      state.successMessages[action.payload] = null;
    },
    
    // Set selected sub-account
    setSelectedSubAccount: (state, action: PayloadAction<string | null>) => {
      state.selectedSubAccountUID = action.payload;
    },
    
    // Set selected team
    setSelectedTeam: (state, action: PayloadAction<string | null>) => {
      state.selectedTeamName = action.payload;
    },
    
    // Reset all state (for logout)
    resetState: () => initialState,
    
    // Update account type info (for integration with existing auth slice)
    updateAccountTypeInfo: (state, action: PayloadAction<{
      accountType: 'master' | 'sub' | 'regular';
      isMasterAccount: boolean;
      isSubAccount: boolean;
      parentMasterUID?: string;
      parentMasterEmail?: string;
    }>) => {
      state.accountType = action.payload.accountType;
      state.isMasterAccount = action.payload.isMasterAccount;
      state.isSubAccount = action.payload.isSubAccount;
      state.parentMasterUID = action.payload.parentMasterUID || null;
      state.parentMasterEmail = action.payload.parentMasterEmail || null;
    },
  },
  extraReducers: (builder) => {
    // ========================================================================
    // SUB-ACCOUNT LOGIN
    // ========================================================================
    builder
      .addCase(subAccountLogin.pending, (state) => {
        state.loading.userInfo = true;
        state.errors.userInfo = null;
      })
      .addCase(subAccountLogin.fulfilled, (state, action) => {
        state.loading.userInfo = false;
        state.accountType = 'sub';
        state.isSubAccount = true;
        state.isMasterAccount = false;
        state.parentMasterUID = action.payload.user.parentMasterUID;
        state.parentMasterEmail = action.payload.user.parentMasterEmail;
      })
      .addCase(subAccountLogin.rejected, (state, action) => {
        state.loading.userInfo = false;
        state.errors.userInfo = (action.payload as MasterSubAccountApiError | undefined)?.message || 'Sub-account login failed';
      });

    // ========================================================================
    // ENHANCED USER INFO
    // ========================================================================
    builder
      .addCase(fetchEnhancedUserInfo.pending, (state) => {
        state.loading.userInfo = true;
        state.errors.userInfo = null;
      })
      .addCase(fetchEnhancedUserInfo.fulfilled, (state, action) => {
        state.loading.userInfo = false;
        state.enhancedUserInfo = action.payload;
        state.accountType = action.payload.accountType;
        state.isMasterAccount = action.payload.isMaster;
        state.isSubAccount = action.payload.isSubAccount;
        
        if (action.payload.parentMasterUID) {
          state.parentMasterUID = action.payload.parentMasterUID;
          state.parentMasterEmail = action.payload.parentMasterEmail || null;
        }
        
        if (action.payload.masterAccountInfo) {
          state.masterAccountInfo = action.payload.masterAccountInfo;
          
          // Build allTeamsData for easy UI consumption
          const allTeams = [
            // Master account team
            {
              id: action.payload.masterAccountInfo.masterAccount.uid || 'master',
              name: (() => {
                // Extract team name from teamName array (API provides teamName, not teams)
                const masterAccount = action.payload.masterAccountInfo.masterAccount;
                if (Array.isArray(masterAccount.teamName) && masterAccount.teamName.length > 0) {
                  return masterAccount.teamName[0];
                } else if (typeof masterAccount.teamName === 'string' && masterAccount.teamName) {
                  return masterAccount.teamName;
                } else if (Array.isArray((masterAccount as any).teams) && (masterAccount as any).teams.length > 0) {
                  // Fallback to teams field if teamName doesn't exist
                  return (masterAccount as any).teams[0];
                } else if (typeof (masterAccount as any).teams === 'string') {
                  return (masterAccount as any).teams;
                }
                return '';
              })(),
              displayName: 'Master Account',
              company: action.payload.masterAccountInfo.masterAccount.company,
              type: 'master' as const,
              isActive: true,
              probesCount: 0, // Will be updated when fetching actual probe data
            },
            // Sub-account teams
            ...action.payload.masterAccountInfo.subAccounts.map(sub => {
              // Determine team name: check teamName array, teams array, or use UID
              let teamName = sub.uid; // Default fallback
              
              if (Array.isArray(sub.teamName) && sub.teamName.length > 0) {
                teamName = sub.teamName[0];
              } else if (typeof sub.teamName === 'string' && sub.teamName) {
                teamName = sub.teamName;
              } else if ((sub as any).teams && Array.isArray((sub as any).teams) && (sub as any).teams.length > 0) {
                // Check 'teams' field (alternate API response format)
                teamName = (sub as any).teams[0];
              }
              
              logger.log('🔍 Sub-account team mapping (enhanced):', {
                uid: sub.uid,
                username: sub.username,
                teamName_field: sub.teamName,
                teams_field: (sub as any).teams,
                resolved_teamName: teamName
              });
              
              return {
                id: sub.uid,
                name: teamName,
                displayName: sub.username, // Use username since there's no separate 'name' field
                company: sub.company,
                type: 'sub' as const,
                isActive: !sub.deactivated,
                probesCount: 0, // Will be fetched separately via fetchTeamProbes
                createdAt: sub.createdAt,
              };
            })
          ];
          
          state.allTeamsData = allTeams;
        }
      })
      .addCase(fetchEnhancedUserInfo.rejected, (state, action) => {
        state.loading.userInfo = false;
        state.errors.userInfo = (action.payload as MasterSubAccountApiError | undefined)?.message || 'Failed to fetch user information';
      });

    // ========================================================================
    // CREATE SUB-ACCOUNT
    // ========================================================================
    builder
      .addCase(createSubAccount.pending, (state) => {
        state.loading.createSubAccount = true;
        state.errors.createSubAccount = null;
        state.successMessages.createSubAccount = null;
      })
      .addCase(createSubAccount.fulfilled, (state, action) => {
        state.loading.createSubAccount = false;
        state.successMessages.createSubAccount = action.payload.message;
        
        // Add new sub-account to masterAccountInfo if it exists
        if (state.masterAccountInfo) {
          state.masterAccountInfo.subAccounts.push(action.payload.subAccount);
          
          // Update allTeamsData
          if (state.allTeamsData) {
            // Determine team name: check teamName array, teams array, or use UID
            const sub = action.payload.subAccount;
            let teamName = sub.uid; // Default fallback
            
            if (Array.isArray(sub.teamName) && sub.teamName.length > 0) {
              teamName = sub.teamName[0];
            } else if (typeof sub.teamName === 'string' && sub.teamName) {
              teamName = sub.teamName;
            } else if ((sub as any).teams && Array.isArray((sub as any).teams) && (sub as any).teams.length > 0) {
              // Check 'teams' field (alternate API response format)
              teamName = (sub as any).teams[0];
            }
            
            logger.log('🔍 New sub-account team mapping:', {
              uid: sub.uid,
              username: sub.username,
              teamName_field: sub.teamName,
              teams_field: (sub as any).teams,
              resolved_teamName: teamName
            });
            
            state.allTeamsData.push({
              id: sub.uid,
              name: teamName,
              displayName: sub.username, // Use username since there's no 'name' field
              company: sub.company,
              type: 'sub',
              isActive: !sub.deactivated,
              probesCount: 0, // New sub-account starts with 0 probes
              createdAt: sub.createdAt,
            });
          }
        }
      })
      .addCase(createSubAccount.rejected, (state, action) => {
        state.loading.createSubAccount = false;
        state.errors.createSubAccount = (action.payload as MasterSubAccountApiError | undefined)?.message || 'Failed to create sub-account';
      });

    // ========================================================================
    // FETCH MY SUB-ACCOUNTS
    // ========================================================================
    builder
      .addCase(fetchMySubAccounts.pending, (state) => {
        state.loading.subAccounts = true;
        state.errors.subAccounts = null;
      })
      .addCase(fetchMySubAccounts.fulfilled, (state, action) => {
        state.loading.subAccounts = false;
        state.masterAccountInfo = {
          masterAccount: action.payload.masterAccount,
          subAccounts: action.payload.subAccounts
        };
        
        // Update allTeamsData
        const allTeams = [
          // Master account team
          {
            id: 'master',
            name: action.payload.masterAccount.teams[0],
            displayName: 'Master Account',
            company: action.payload.masterAccount.company,
            type: 'master' as const,
            isActive: true,
            probesCount: 0,
          },
          // Sub-account teams
          ...action.payload.subAccounts.map(sub => {
            // Determine team name: check teamName array, teams array, or use UID
            let teamName = sub.uid; // Default fallback
            
            if (Array.isArray(sub.teamName) && sub.teamName.length > 0) {
              teamName = sub.teamName[0];
            } else if (typeof sub.teamName === 'string' && sub.teamName) {
              teamName = sub.teamName;
            } else if ((sub as any).teams && Array.isArray((sub as any).teams) && (sub as any).teams.length > 0) {
              // Check 'teams' field (alternate API response format)
              teamName = (sub as any).teams[0];
            }
            
            logger.log('🔍 Sub-account team mapping:', {
              uid: sub.uid,
              username: sub.username,
              teamName_field: sub.teamName,
              teams_field: (sub as any).teams,
              resolved_teamName: teamName
            });
            
            return {
              id: sub.uid,
              name: teamName,
              displayName: sub.username, // Use username since there's no separate 'name' field
              company: sub.company,
              type: 'sub' as const,
              isActive: !sub.deactivated,
              probesCount: 0, // Will be fetched separately via fetchTeamProbes
              createdAt: sub.createdAt,
            };
          })
        ];
        
        state.allTeamsData = allTeams;
      })
      .addCase(fetchMySubAccounts.rejected, (state, action) => {
        state.loading.subAccounts = false;
        state.errors.subAccounts = (action.payload as MasterSubAccountApiError | undefined)?.message || 'Failed to fetch sub-accounts';
      });

    // ========================================================================
    // TOGGLE SUB-ACCOUNT STATUS
    // ========================================================================
    builder
      .addCase(toggleSubAccountStatus.pending, (state) => {
        state.loading.toggleStatus = true;
        state.errors.toggleStatus = null;
        state.successMessages.toggleStatus = null;
      })
      .addCase(toggleSubAccountStatus.fulfilled, (state, action) => {
        state.loading.toggleStatus = false;
        state.successMessages.toggleStatus = action.payload.message;
        
        // Update the sub-account status in masterAccountInfo
        if (state.masterAccountInfo) {
          const subAccountIndex = state.masterAccountInfo.subAccounts.findIndex(
            sub => sub.uid === action.payload.subAccountUID
          );
          if (subAccountIndex !== -1) {
            state.masterAccountInfo.subAccounts[subAccountIndex].deactivated = 
              action.payload.subAccount.deactivated;
          }
        }
        
        // Update allTeamsData
        if (state.allTeamsData) {
          const teamIndex = state.allTeamsData.findIndex(
            team => team.id === action.payload.subAccountUID
          );
          if (teamIndex !== -1) {
            state.allTeamsData[teamIndex].isActive = !action.payload.subAccount.deactivated;
          }
        }
      })
      .addCase(toggleSubAccountStatus.rejected, (state, action) => {
        state.loading.toggleStatus = false;
        state.errors.toggleStatus = (action.payload as MasterSubAccountApiError | undefined)?.message || 'Failed to toggle sub-account status';
      });

    // ========================================================================
    // CHANGE SUB-ACCOUNT PASSWORD
    // ========================================================================
    builder
      .addCase(changeSubAccountPassword.pending, (state) => {
        state.loading.changePassword = true;
        state.errors.changePassword = null;
        state.successMessages.changePassword = null;
      })
      .addCase(changeSubAccountPassword.fulfilled, (state, action) => {
        state.loading.changePassword = false;
        state.successMessages.changePassword = action.payload.message;
      })
      .addCase(changeSubAccountPassword.rejected, (state, action) => {
        state.loading.changePassword = false;
        state.errors.changePassword = (action.payload as MasterSubAccountApiError | undefined)?.message || 'Failed to change sub-account password';
      });

    // ========================================================================
    // DELETE SUB-ACCOUNT
    // ========================================================================
    builder
      .addCase(deleteSubAccount.pending, (state) => {
        state.loading.deleteSubAccount = true;
        state.errors.deleteSubAccount = null;
        state.successMessages.deleteSubAccount = null;
      })
      .addCase(deleteSubAccount.fulfilled, (state, action) => {
        state.loading.deleteSubAccount = false;
        state.successMessages.deleteSubAccount = `${action.payload.message}. ${action.payload.trapsMovedBack} SmartProbes moved back to master account.`;
        
        // Remove the sub-account from masterAccountInfo
        if (state.masterAccountInfo) {
          state.masterAccountInfo.subAccounts = state.masterAccountInfo.subAccounts.filter(
            sub => sub.uid !== action.payload.deletedSubAccountUID
          );
        }
        
        // Remove from allTeamsData
        if (state.allTeamsData) {
          state.allTeamsData = state.allTeamsData.filter(
            team => team.id !== action.payload.deletedSubAccountUID
          );
        }
        
        // Clear selected sub-account if it was the deleted one
        if (state.selectedSubAccountUID === action.payload.deletedSubAccountUID) {
          state.selectedSubAccountUID = null;
        }
      })
      .addCase(deleteSubAccount.rejected, (state, action) => {
        state.loading.deleteSubAccount = false;
        state.errors.deleteSubAccount = (action.payload as MasterSubAccountApiError | undefined)?.message || 'Failed to delete sub-account';
      });

    // ========================================================================
    // MOVE SMARTPROBE
    // ========================================================================
    builder
      .addCase(moveSmartProbe.pending, (state) => {
        state.loading.moveProbe = true;
        state.errors.moveProbe = null;
        state.successMessages.moveProbe = null;
      })
      .addCase(moveSmartProbe.fulfilled, (state, action) => {
        state.loading.moveProbe = false;
        state.successMessages.moveProbe = action.payload.message;
        
        // Update probe counts in allTeamsData if available
        if (state.allTeamsData && action.payload.teams) {
          const fromTeamIndex = state.allTeamsData.findIndex(
            team => team.name === action.payload.teams.fromTeam.name
          );
          const toTeamIndex = state.allTeamsData.findIndex(
            team => team.name === action.payload.teams.toTeam.name
          );
          
          if (fromTeamIndex !== -1) {
            state.allTeamsData[fromTeamIndex].probesCount = action.payload.teams.fromTeam.remainingProbes;
          }
          
          if (toTeamIndex !== -1) {
            state.allTeamsData[toTeamIndex].probesCount = action.payload.teams.toTeam.totalProbes;
          }
        }
      })
      .addCase(moveSmartProbe.rejected, (state, action) => {
        state.loading.moveProbe = false;
        state.errors.moveProbe = (action.payload as MasterSubAccountApiError | undefined)?.message || 'Failed to move SmartProbe';
      });

    // ========================================================================
    // FETCH TEAM PROBES
    // ========================================================================
    builder
      .addCase(fetchTeamProbes.pending, (state) => {
        state.loading.teamProbes = true;
        state.errors.teamProbes = null;
      })
      .addCase(fetchTeamProbes.fulfilled, (state, action) => {
        state.loading.teamProbes = false;
        state.teamProbesData = action.payload.teams;
        state.selectedTeamName = action.payload.teamName;
      })
      .addCase(fetchTeamProbes.rejected, (state, action) => {
        state.loading.teamProbes = false;
        state.errors.teamProbes = (action.payload as MasterSubAccountApiError | undefined)?.message || 'Failed to fetch team probes';
      });
  },
});

// ============================================================================
// SELECTORS
// ============================================================================

export const selectAccountType = (state: { masterSubAccount: MasterSubAccountState }) => 
  state.masterSubAccount.accountType;

export const selectIsMasterAccount = (state: { masterSubAccount: MasterSubAccountState }) => 
  state.masterSubAccount.isMasterAccount;

export const selectIsSubAccount = (state: { masterSubAccount: MasterSubAccountState }) => 
  state.masterSubAccount.isSubAccount;

export const selectMasterAccountInfo = (state: { masterSubAccount: MasterSubAccountState }) => 
  state.masterSubAccount.masterAccountInfo;

export const selectEnhancedUserInfo = (state: { masterSubAccount: MasterSubAccountState }) => 
  state.masterSubAccount.enhancedUserInfo;

export const selectAllTeamsData = (state: { masterSubAccount: MasterSubAccountState }) => 
  state.masterSubAccount.allTeamsData;

export const selectTeamProbesData = (state: { masterSubAccount: MasterSubAccountState }) => 
  state.masterSubAccount.teamProbesData;

export const selectSelectedSubAccount = (state: { masterSubAccount: MasterSubAccountState }) => {
  const { selectedSubAccountUID, masterAccountInfo } = state.masterSubAccount;
  if (!selectedSubAccountUID || !masterAccountInfo) return null;
  
  return masterAccountInfo.subAccounts.find(sub => sub.uid === selectedSubAccountUID) || null;
};

export const selectParentMasterInfo = (state: { masterSubAccount: MasterSubAccountState }) => ({
  uid: state.masterSubAccount.parentMasterUID,
  email: state.masterSubAccount.parentMasterEmail
});

export const selectLoadingStates = (state: { masterSubAccount: MasterSubAccountState }) => 
  state.masterSubAccount.loading;

export const selectErrors = (state: { masterSubAccount: MasterSubAccountState }) => 
  state.masterSubAccount.errors;

export const selectSuccessMessages = (state: { masterSubAccount: MasterSubAccountState }) => 
  state.masterSubAccount.successMessages;

// ============================================================================
// EXPORT ACTIONS AND REDUCER
// ============================================================================

export const {
  clearErrors,
  clearError,
  clearSuccessMessages,
  clearSuccessMessage,
  setSelectedSubAccount,
  setSelectedTeam,
  resetState,
  updateAccountTypeInfo,
} = masterSubAccountSlice.actions;

export default masterSubAccountSlice.reducer;


